import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { HighlightTemplate, SlideLayoutProps } from '../types';
import {
  BodyContent,
  LEADING,
  PADDING,
  SlideText,
  TYPE,
  fitTitle,
  formatPageLabel,
  hasNextSlide,
  shouldShowCounter,
  TwoToneTitle,
} from '../_shared';

// "Punch Magazine" - bold uppercase display type on a dotted surface.
// Cover and even-index slides sit on cream with an orange title and a
// floating arrow disc; odd-index slides invert to a full-bleed orange
// surface with a black title and white body text.

const CREAM = '#EAE7DE';
const ORANGE = '#E96B26';
const INK = '#0A0A0A';

const DOT_BG_LIGHT: React.CSSProperties = {
  backgroundColor: CREAM,
  backgroundImage: `radial-gradient(circle, rgba(0,0,0,0.14) 1.1px, transparent 1.1px)`,
  backgroundSize: '22px 22px',
};

const DOT_BG_ORANGE: React.CSSProperties = {
  backgroundColor: ORANGE,
  backgroundImage: `radial-gradient(circle, rgba(0,0,0,0.16) 1.1px, transparent 1.1px)`,
  backgroundSize: '22px 22px',
};

const DOT_BG_THUMB: React.CSSProperties = {
  backgroundColor: CREAM,
  backgroundImage: DOT_BG_LIGHT.backgroundImage,
  backgroundSize: '10px 10px',
};

function isOrangeSlide(slideIndex: number): boolean {
  return slideIndex % 2 === 1;
}

function ArrowDisc({
  bgColor,
  iconColor,
}: {
  bgColor: string;
  iconColor: string;
}) {
  return (
    <div
      className='pointer-events-none flex h-16 w-16 items-center justify-center rounded-full md:h-20 md:w-20'
      style={{ backgroundColor: bgColor }}
      aria-hidden='true'
    >
      <ArrowRight
        className='h-7 w-7 md:h-9 md:w-9'
        style={{ color: iconColor }}
        strokeWidth={3.5}
      />
    </div>
  );
}

function TopBar({
  conceptName,
  pageLabel,
  inkColor,
}: {
  conceptName?: string;
  pageLabel?: string;
  inkColor: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between font-bold uppercase tracking-[0.15em]',
        TYPE.topBar,
        PADDING.slideX,
        'pt-8 md:pt-10'
      )}
      style={{ color: inkColor }}
    >
      <SlideText className='truncate pr-4' text={conceptName ?? ''} />
      {pageLabel && <span className='tabular-nums'>{pageLabel}</span>}
    </div>
  );
}

function CoverLayout({
  card,
  slideIndex,
  totalSlides,
  conceptName,
}: SlideLayoutProps) {
  return (
    <div className='relative h-full w-full' style={DOT_BG_LIGHT}>
      <div
        className='absolute right-0 top-0 h-full w-[5%]'
        style={{ backgroundColor: ORANGE }}
        aria-hidden='true'
      />

      <div className='relative flex h-full w-full flex-col pr-[7%]'>
        <TopBar conceptName={conceptName} inkColor={INK} />

        <div
          className={cn('flex flex-1 flex-col justify-center', PADDING.slideX)}
        >
          <TwoToneTitle
            title={card.title}
            as='h1'
            gap='mt-1'
            tones={[ORANGE, INK]}
            className={cn(
              'text-balance font-extrabold uppercase tracking-tight',
              fitTitle(card.title),
              LEADING.display
            )}
          />
        </div>

        <div
          className={cn(
            'flex items-end justify-end pb-8 md:pb-10',
            PADDING.slideX
          )}
        >
          {hasNextSlide(slideIndex, totalSlides) && (
            <ArrowDisc bgColor={ORANGE} iconColor={INK} />
          )}
        </div>
      </div>
    </div>
  );
}

function ContentLayout({
  card,
  slideIndex,
  totalSlides,
  conceptName,
}: SlideLayoutProps) {
  const orange = isOrangeSlide(slideIndex);
  const surfaceStyle = orange ? DOT_BG_ORANGE : DOT_BG_LIGHT;
  const stripeColor = orange ? CREAM : ORANGE;
  const titleColor = orange ? INK : ORANGE;
  const bodyColor = orange ? '#FFFFFF' : INK;
  const arrowBg = orange ? INK : ORANGE;
  const arrowIcon = orange ? '#FFFFFF' : INK;
  const showCounter = shouldShowCounter(slideIndex, totalSlides);
  const showArrow = hasNextSlide(slideIndex, totalSlides);
  const pageLabel = showCounter
    ? formatPageLabel(slideIndex, totalSlides)
    : undefined;

  return (
    <div className='relative h-full w-full' style={surfaceStyle}>
      <div
        className='absolute right-0 top-0 h-full w-[5%]'
        style={{ backgroundColor: stripeColor }}
        aria-hidden='true'
      />

      <div className='relative flex h-full w-full flex-col pr-[7%]'>
        <TopBar
          conceptName={conceptName}
          pageLabel={pageLabel}
          inkColor={INK}
        />

        <div
          className={cn(
            'flex flex-1 flex-col gap-6 pt-8 md:pt-12',
            PADDING.slideX
          )}
        >
          <h2
            className={cn(
              'text-balance font-extrabold uppercase tracking-tight',
              fitTitle(card.title, 'body'),
              LEADING.display
            )}
            style={{ color: titleColor }}
          >
            <SlideText text={card.title} />
          </h2>
          {card.body && (
            <BodyContent
              body={card.body}
              visual={card.visual}
              className='max-w-md font-semibold'
              style={{ color: bodyColor }}
              spacing='space-y-2'
            />
          )}
        </div>

        <div
          className={cn(
            'flex items-end justify-end pb-8 md:pb-10',
            PADDING.slideX
          )}
        >
          {showArrow && <ArrowDisc bgColor={arrowBg} iconColor={arrowIcon} />}
        </div>
      </div>
    </div>
  );
}

function ThumbnailLayout({ card, totalSlides }: SlideLayoutProps) {
  return (
    <div className='relative h-full w-full' style={DOT_BG_THUMB}>
      <div
        className='absolute right-0 top-0 h-full w-[5%]'
        style={{ backgroundColor: ORANGE }}
        aria-hidden='true'
      />

      <div className='relative flex h-full flex-col p-5 pr-[10%]'>
        <div className='flex flex-1 flex-col justify-center'>
          <TwoToneTitle
            title={card.title}
            as='h3'
            gap='mt-0.5'
            tones={[ORANGE, INK]}
            className={cn(
              'line-clamp-2 font-extrabold uppercase tracking-tight',
              fitTitle(card.title, 'thumb'),
              LEADING.display
            )}
          />
        </div>

        {totalSlides > 1 && (
          <div className='self-end'>
            <div
              className='flex h-7 w-7 items-center justify-center rounded-full'
              style={{ backgroundColor: ORANGE }}
              aria-hidden='true'
            >
              <ArrowRight
                className='h-3.5 w-3.5'
                style={{ color: INK }}
                strokeWidth={3.5}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export const Template04: HighlightTemplate = {
  id: 'template-04',
  name: 'Punch Magazine',
  layouts: {
    cover: CoverLayout,
    body: ContentLayout,
    summary: ContentLayout,
  },
  thumbnail: ThumbnailLayout,
};
